"use client";

import { useMemo } from "react";

import roundArrow from "@repo/ui/assets/common/button/Round_Arrow.svg";
import ModalInfoIcon from "@repo/ui/assets/icons/ModalInfoIcon";
import { JetSmartButton } from "@repo/ui/components/common/forms/JetSmartButton";
import { cn } from "@repo/ui/lib/utils";

import { CommonDialog } from "./CommonDialog";

interface SessionTimeoutModalProps {
  open: boolean;
  remainingSeconds: number;
  onExtend: () => void;
  onLogout: () => void;
  // Text customization props for internationalization
  expiredTitle?: string;
  activeTitle?: string;
  expiredDescription?: string;
  activeDescription?: string;
  extendButtonText?: string;
  logoutButtonText?: string;
  isLoggingOut?: boolean;
  logoutButtonLoadingText?: string;
}

export function SessionTimeoutModal({
  open,
  remainingSeconds,
  onExtend,
  onLogout,
  expiredTitle = "Your session is expired.",
  activeTitle = "Your session is about to end.",
  expiredDescription = "Your session has expired.",
  activeDescription = "Your session has expired.",
  extendButtonText = "Extend session",
  logoutButtonText = "Start again",
  isLoggingOut = false,
  logoutButtonLoadingText = "Starting...",
}: Readonly<SessionTimeoutModalProps>) {
  const { minutes, seconds } = useMemo(() => {
    const totalSeconds = Math.max(0, remainingSeconds);
    return {
      minutes: Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0"),
      seconds: (totalSeconds % 60).toString().padStart(2, "0"),
    };
  }, [remainingSeconds]);

  const isExpired = remainingSeconds <= 0;

  return (
    <CommonDialog
      open={open}
      onOpenChange={() => {}} // Prevent closing by clicking outside
      contentClassName="p-2"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      showCloseButton={false}
      title={
        <div className="flex items-center gap-2">
          <ModalInfoIcon
            width={20}
            height={20}
            className={cn("cursor-pointer")}
            iconFill="var(--brand-turquoise)"
          />
          <span className="text-lg font-bold text-black">
            {isExpired ? expiredTitle : `${activeTitle} (${minutes}:${seconds})`}
          </span>
        </div>
      }
      titleClassName="bg-[#DEF3F7] p-4 rounded-xl text-black mx-4"
      description={isExpired ? expiredDescription : activeDescription}
      descriptionClassName="text-black py-4 px-0 text-base px-4"
      primaryButton={
        <JetSmartButton
          iconPosition="right"
          iconSrc={roundArrow}
          iconAlt="Round Arrow"
          iconClassName="w-5 h-5 text-white"
          className="bg-destructive h-auto p-3 px-4 text-white"
          onClick={isExpired ? onLogout : onExtend}
          disabled={isLoggingOut}
        >
          {isExpired
            ? isLoggingOut
              ? logoutButtonLoadingText
              : logoutButtonText
            : extendButtonText}
        </JetSmartButton>
      }
      footerClassName="p-4 pt-0"
    />
  );
}

export default SessionTimeoutModal;
